import {fetchAuthSession, signIn as amplifySignIn, signOut as amplifySignOut} from 'aws-amplify/auth';

import appConfig from '../../configs/appConfig';
import userChannelNames from '../../consts/auth/userChannelNames';
import userRoleNames from '../../consts/auth/userRoleNames';
import awsAuthErrorMessages from '../../consts/aws/awsAuthErrorMessages';
import awsAuthErrorTypes from '../../consts/aws/awsAuthErrorTypes';
import envVariables from '../../consts/envVariables';
import {strCode} from '../../consts/strCode';
import AuthSessionAccessError from '../../errors/authSessionAccessError';
import InactiveSessionError from '../../errors/inactiveSessionError';
import {selectIsAuthorized, selectUserGroups} from '../../state/selectors/auth';
import {selectIsDevMockSession, selectIsRetail} from '../../state/selectors/global';
import {selectIsMandatoryConsentsAccepted} from '../../state/selectors/session';
import {resetAiAssistantState} from '../../state/slice/aiAssistant';
import {
    clearAuthData,
    setAuthTime,
    setFirstName,
    setIsAuthorized,
    setLastName,
    setLocationId,
    setUserChannel,
    setUserGroups,
    setUserId,
    setUserName,
    setUserRoles,
} from '../../state/slice/auth';
import {clearNotifications} from '../../state/slice/global';
import {clearDevices, setCurrentDusn} from '../../state/slice/iotDevices';
import {hideAllModals} from '../../state/slice/modal';
import {setIsRetailDevicesLoading} from '../../state/slice/retail';
import {
    clearConsents,
    resetActiveDeviceNavigationMenuType,
    setIsDevicesInitialized,
    setIsUserSessionInitialized,
} from '../../state/slice/session';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import cacheService from '../cacheService';
import configService from '../config/configService';
import consentsService from '../consentsService';
import deviceService from '../device/deviceService';
import channelService from '../features/channelService';
import iapClientService from '../iap/iapClientService';
import log from '../logger/log';
import completionMessageQueue from '../loyalty/loyaltyMessageQueueService';
import loyaltyService from '../loyalty/loyaltyService';
import notificationService from '../notificationService';
import pushNotificationService from '../pushNotificationService';
import retailService from '../retail/retailService';
import serviceWorkerService from '../serviceWorkerService';
import setupGuideService from '../setupGuideService';
import uamWebSocketService from '../uam/uamWebSocketService';
import userSessionManagementService from '../userSessionManagementService';
import voxlinkSessionService from '../voxlink/voxlinkSessionService';
import {loadCurrentTokens} from './cognitoTokenService';
import awsRumService from './rum/awsRum';

let isSigningOut = false;
const UNSUPPORTED_SIGN_IN_STEP = 'UnsupportedSignInStep';
const NO_CURRENT_USER_ERROR_NAMES = new Set(['UserUnAuthenticatedException']);
const NO_CURRENT_USER_ERROR_MESSAGES = new Set([
    awsAuthErrorMessages.NO_CURRENT_USER,
    'User needs to be authenticated to call this API.',
]);

const getTokenValue = (token) => {
    if (!token) return null;
    if (typeof token === 'string') return token;
    if (typeof token?.toString === 'function') return token.toString();
    return token?.token || null;
};

const createLegacyJwtToken = (token) => {
    const jwtToken = getTokenValue(token);

    if (!jwtToken) return null;

    return {
        payload: token?.payload,
        jwtToken,
        getJwtToken: () => jwtToken,
        toString: () => jwtToken,
    };
};

const createLegacyRefreshToken = (refreshToken) => {
    if (!refreshToken) return null;

    return {
        token: refreshToken,
        getToken: () => refreshToken,
    };
};

const getAuthStorage = () => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const getStoredLastAuthUser = () => {
    const storage = getAuthStorage();
    const userPoolClientId = envVariables.AWS_AUTH_USER_POOL_WEB_CLIENT_ID;

    if (!storage || !userPoolClientId) return null;

    return storage.getItem(`CognitoIdentityServiceProvider.${userPoolClientId}.LastAuthUser`);
};

const getStoredRefreshToken = (username) => {
    const storage = getAuthStorage();
    const userPoolClientId = envVariables.AWS_AUTH_USER_POOL_WEB_CLIENT_ID;
    const resolvedUsername = username || getStoredLastAuthUser();

    if (!storage || !userPoolClientId || !resolvedUsername) return null;

    return storage.getItem(`CognitoIdentityServiceProvider.${userPoolClientId}.${resolvedUsername}.refreshToken`);
};

const getRefreshTokenValue = (tokens, username) =>
    getTokenValue(tokens.refreshToken) || getStoredRefreshToken(username);

const normalizeAuthSession = (tokens) => {
    const idToken = createLegacyJwtToken(tokens.idToken);
    const accessToken = createLegacyJwtToken(tokens.accessToken);
    const username = idToken?.payload?.['cognito:username'] || null;
    const refreshToken = createLegacyRefreshToken(getRefreshTokenValue(tokens, username));

    return {
        accessToken,
        idToken,
        refreshToken,
        tokens,
        userSub: accessToken?.payload?.sub || null,
        username,
        getAccessToken: () => accessToken,
        getIdToken: () => idToken,
        getRefreshToken: () => refreshToken,
    };
};

const getIsNoCurrentUserError = (error) => {
    const errorMessage = typeof error === 'string' ? error : error?.message;

    return (
        error === awsAuthErrorMessages.NO_CURRENT_USER ||
        NO_CURRENT_USER_ERROR_NAMES.has(error?.name) ||
        NO_CURRENT_USER_ERROR_MESSAGES.has(errorMessage)
    );
};

const getCurrentAmplifySession = async () => {
    const tokens = await loadCurrentTokens();

    if (!tokens?.accessToken) throw new Error(awsAuthErrorMessages.NO_CURRENT_USER);

    return normalizeAuthSession(tokens);
};

const getCurrentSession = () => handleAuthError(() => getCurrentAmplifySession());
const getCredentials = () => handleAuthError(async () => (await fetchAuthSession())?.credentials || null);

const handleAuthError = async (callback) => {
    try {
        return await callback();
    } catch (e) {
        if (getIsNoCurrentUserError(e)) {
            const isAuthorized = selectIsAuthorized(getState());
            const isDevMockSession = selectIsDevMockSession(getState());

            if (isAuthorized && !isSigningOut && !isDevMockSession) {
                log.error('awsAuthService: No current user error');

                await signOut(() => notificationService.showSessionExpiredError());
            }
        }

        throw e;
    }
};

const getNormalizedAuthFlowType = () => {
    const authFlowType = envVariables.AWS_AUTH_AUTHENTICATION_FLOW_TYPE;

    if (!authFlowType) return null;
    if (authFlowType === 'CUSTOM_AUTH') return 'CUSTOM_WITH_SRP';

    return authFlowType;
};

const createSignInInput = (userName, password) => {
    const authFlowType = getNormalizedAuthFlowType();

    if (!authFlowType) {
        return {username: userName, password};
    }

    return {
        username: userName,
        password,
        options: {
            authFlowType,
        },
    };
};

const createUnsupportedSignInStepError = (nextStep) =>
    Object.assign(new Error(`Unsupported sign-in step: ${nextStep?.signInStep || 'UNKNOWN'}`), {
        code: UNSUPPORTED_SIGN_IN_STEP,
        nextStep,
    });

const getCognitoIdToken = async () => {
    try {
        const session = await getCurrentSession();

        return session.getIdToken().getJwtToken();
    } catch (e) {
        log.error('awsAuthService: Failed to get cognito id token', e.message);

        throw e;
    }
};

const getCognitoAccessToken = async () => {
    try {
        const session = await getCurrentSession();

        return session.getAccessToken().getJwtToken();
    } catch (e) {
        log.error('awsAuthService: Failed to get cognito access token', e.message);

        throw e;
    }
};

const getCognitoUserEmail = async () => {
    try {
        const session = await getCurrentSession();

        return session.getIdToken()?.payload?.email || '';
    } catch (e) {
        log.warn('awsAuthService: Failed to get cognito user email', e.message);

        return '';
    }
};

const getCognitoRefreshToken = async () => {
    try {
        const session = await getCurrentSession();

        return {refreshToken: session?.refreshToken?.token, idToken: session?.idToken?.jwtToken};
    } catch (e) {
        log.warn('awsAuthService: Failed to get cognito refresh token', e.message);
        return {refreshToken: null, idToken: null};
    }
};

const signIn = async (userName, password) => {
    try {
        const isUserAlreadyAuthorized = await checkAuthorization(false);

        if (isUserAlreadyAuthorized) return;

        const result = await amplifySignIn(createSignInInput(userName, password));

        if (!result?.isSignedIn) {
            throw createUnsupportedSignInStepError(result?.nextStep);
        }

        const session = await getCurrentSession();
        const refreshToken = session?.getRefreshToken()?.getToken?.();

        await userSessionManagementService.createUserSession(refreshToken);

        processUserSession(session);

        await cacheService.clearCaches();

        serviceWorkerService.postMessageUserIsAuthorized(true);

        await processSessionData();

        log.info('awsAuthService: Successful login');
    } catch (e) {
        log.error(`awsAuthService: Login failed, message: ${e?.message}, code: ${e?.code}`);

        await handleSignInError(e);

        throw e;
    }
};

const handleSignInError = async (error) => {
    if (error instanceof AuthSessionAccessError) {
        await signOut(() => notificationService.showSessionAccessError());
        return;
    }
    const {code, message} = error || {};

    if (code === UNSUPPORTED_SIGN_IN_STEP) {
        log.warn(`awsAuthService: Unsupported sign-in step: ${error?.nextStep?.signInStep}`);
        notificationService.showToastNotification({messageStr: strCode.LOGIN_PAGE_ERROR_PASSWORD_OR_USER_NOT_EXIST});
        return;
    }

    if (message && code === awsAuthErrorTypes.USER_LAMBDA_VALIDATION_EXCEPTION) {
        handleUserLambdaValidationException(message);
        return;
    }
    notificationService.showToastNotification({messageStr: strCode.LOGIN_PAGE_ERROR_PASSWORD_OR_USER_NOT_EXIST});
};

const handleUserLambdaValidationException = (message) => {
    const lambdaValidationErrors = [
        {
            messageType: awsAuthErrorMessages.USER_IS_LOCKED_OUT,
            notificationStr: strCode.LOGIN_PAGE_ERROR_TOO_MANY_ATTEMPTS,
            logMessage: 'awsAuthService: User is locked out',
        },
        {
            messageType: awsAuthErrorMessages.USER_PASSWORD_EXPIRED,
            notificationStr: strCode.LOGIN_PAGE_ERROR_USER_PASSWORD_EXPIRED,
            logMessage: 'awsAuthService: User password expired',
        },
    ];

    const found = lambdaValidationErrors.find(({messageType}) => message.includes(messageType));

    if (found) {
        notificationService.showToastNotification({messageStr: found.notificationStr});
        log.warn(found.logMessage);
        return;
    }

    log.warn(`awsAuthService: Unknown lambda validation error: ${message}`);
    notificationService.showToastNotification({messageStr: strCode.LOGIN_PAGE_ERROR_PASSWORD_OR_USER_NOT_EXIST});
};

const _performSignOut = async (callback) => {
    log.info('awsAuthService: Starting logout process');

    awsRumService.disable();
    awsRumService.resetAnalyticsSession();

    try {
        isSigningOut = true;
        // the gateway contract requires its logout before the Cognito one
        await voxlinkSessionService.endSession();
        const {refreshToken, idToken} = await awsAuthService.getCognitoRefreshToken();

        loyaltyService.cancelAllBadgesRetries();
        await pushNotificationService.unsubscribe();
        await amplifySignOut();
        await iapClientService.postUserLogout(refreshToken, idToken);
        uamWebSocketService.disconnectAllWebSockets();
        awsRumService.setAwsCredentials();
        serviceWorkerService.postMessageUserIsAuthorized(false);
        serviceWorkerService.postMessageRemoveNotificationsAndBadges();
        cacheService.clearCaches();
        configService.resetConfig();
        dispatch(clearAuthData());
        dispatch(clearDevices());
        dispatch(clearNotifications());
        dispatch(setCurrentDusn(null));
        dispatch(clearConsents());
        dispatch(setIsDevicesInitialized(false));
        dispatch(setIsUserSessionInitialized(false));
        dispatch(resetActiveDeviceNavigationMenuType());
        dispatch(resetAiAssistantState());
        dispatch(hideAllModals());
        callback?.();
        log.info('awsAuthService: Successful logout');
    } catch (e) {
        log.error('awsAuthService: Logout failed', e);
    } finally {
        isSigningOut = false;
    }
};

// Prevents concurrent logout processes to avoid duplicate cleanup operations
const performSignOutLocked = helpers.disallowParallelExecution(_performSignOut);

const signOut = (callback) => performSignOutLocked(callback);

const checkAuthorization = async (shouldHandleError = true) => {
    try {
        const session = await getCurrentSession();

        if (!session) return;

        processUserSession(session);
        await userSessionManagementService.validateUserSession();

        const isAuthorized = selectIsAuthorized(getState());

        if (!isAuthorized) {
            log.debug('awsAuthService: User is no longer authorized after session validation');
            return false;
        }

        log.debug('awsAuthService: Processing session data for authorization check');

        await processSessionData();

        log.info('awsAuthService: User is authorized');

        return true;
    } catch (e) {
        log.warn('awsAuthService: User is not authorized', e?.message);

        if (shouldHandleError) await handleAuthenticationError(e);
    }
};

const _performAuthenticationErrorHandler = async (error) => {
    const handlers = [
        {
            condition: error instanceof AuthSessionAccessError,
            action: () => notificationService.showSessionAccessError(),
        },
        {
            condition: error instanceof InactiveSessionError,
            action: () => notificationService.showInactiveSessionError(),
        },
        {
            condition: error?.code === awsAuthErrorTypes.NOT_AUTHORIZED_EXCEPTION,
            action: () => notificationService.showSessionExpiredError(),
        },
    ];

    for (const {condition, action} of handlers) {
        if (!condition) continue;

        await signOut(action);
        break;
    }
};

// Prevents concurrent authentication error handling to avoid duplicate logout flows
const performAuthenticationErrorHandlerLocked = helpers.disallowParallelExecution(_performAuthenticationErrorHandler);

const handleAuthenticationError = (error) => performAuthenticationErrorHandlerLocked(error);

const processUserSession = (session) => {
    const {payload} = session.getIdToken() || {};

    updateUserData(payload);
    validateIdToken(payload);
    pushNotificationService.updatePushNotificationPermission();
};

const updateUserData = (idToken) => {
    dispatch(setIsAuthorized(true));
    dispatch(setFirstName(idToken?.firstName));
    dispatch(setLastName(idToken?.lastName));
    dispatch(setUserName(idToken?.['cognito:username']));
    dispatch(setUserId(idToken?.sub));
    dispatch(setAuthTime(new Date(Number(idToken?.auth_time) * 1000).getTime()));
    dispatch(setLocationId(idToken?.locationId));
};
const AVAILABILITY_RULES = [
    {role: userRoleNames.RETAIL_OPERATOR, channel: userChannelNames.COACH},
    {role: userRoleNames.NEXTGEN, channel: userChannelNames.CONSUMER},
];

const validateIdToken = (idToken) => {
    let assignedGroups = [];
    let userRoles = [];

    try {
        assignedGroups = JSON.parse(idToken?.assignedGroups || '[]');
        userRoles = assignedGroups.filter((x) => x?.appType === envVariables.APP_TYPE).map(({name}) => name);
        const targetGroups = JSON.parse(idToken?.targetGroups || '[]');

        dispatch(setUserRoles(userRoles));
        dispatch(setUserGroups(targetGroups));
        dispatch(setUserChannel(idToken?.channel));
    } catch (e) {
        throw new AuthSessionAccessError('Error parsing assignedGroups');
    }

    const rolesSet = new Set(userRoles);
    const channel = idToken?.channel;
    const isAvailable = AVAILABILITY_RULES.some(({role, channel: ch}) => rolesSet.has(role) && channel === ch);

    if (!isAvailable) {
        throw new AuthSessionAccessError('User roles/channel are not allowed for this app');
    }

    if (!assignedGroups?.length) {
        throw new AuthSessionAccessError('AssignedGroups is empty');
    }

    const group = assignedGroups.find((x) => x?.appType === envVariables.APP_TYPE);

    if (!group) {
        throw new AuthSessionAccessError('App type does not match configuration');
    }

    const {name, markets, activeFrom, activeTo} = group;

    const selectedMarket = markets.length > 0 ? markets[0] : null;

    appConfig.setMarket(selectedMarket);
    appConfig.setUserGroup(name);
    if (!selectedMarket) {
        throw new AuthSessionAccessError('Market does not match configuration');
    }

    const currentDate = new Date();
    const isSessionActive = currentDate >= activeFrom && currentDate <= activeTo;

    if (!isSessionActive) {
        throw new AuthSessionAccessError('Session is inactive');
    }
};

const processSessionData = async () => {
    await configService.handleSessionConfig(selectUserGroups(getState()));
    channelService.setAppChannelType();
    await consentsService.getConsents();
    await initializeUserData();
    completionMessageQueue.initQueue();
};

const initializeUserData = async () => {
    const isMandatoryConsentsAccepted = selectIsMandatoryConsentsAccepted(getState());

    if (!isMandatoryConsentsAccepted) {
        log.info('awsAuthService: Mandatory consents are not accepted, skipping user data initialization');
        return;
    }

    const isRetail = selectIsRetail(getState());

    if (isRetail) {
        log.info('awsAuthService: Initializing user data for retail');

        dispatch(setIsRetailDevicesLoading(true));

        await retailService.getRetailData();
    } else {
        log.info('awsAuthService: Initializing user data');

        loyaltyService.getBadgeData({isThrowError: true}); // To reduce the sign-in time

        await Promise.allSettled([deviceService.initUserDevices(), loyaltyService.getUserConversionStatus()]);
        await loyaltyService.getStoredBadgeData(); // NOTE: Remove after loyalty refactoring

        setupGuideService.checkShouldSetupGuideBeShown();
    }

    dispatch(setIsUserSessionInitialized(true));
};

const awsAuthService = {
    signIn,
    signOut,
    checkAuthorization,
    getCurrentSession,
    getCredentials,
    getCognitoIdToken,
    getCognitoAccessToken,
    getCognitoUserEmail,
    getCognitoRefreshToken,
    handleAuthenticationError,
    initializeUserData,
};

export default awsAuthService;
